<script setup lang="ts">
import { COMMON_RESET_PAGE_ON, commonFilterFields, enumArrayField, PaginationComponent, useQueryFilter } from '@metapic-apps/shared'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useFileDownload, useGetCampaigns } from '@/api'
import { useGetNotificationStatuses } from '@/api/notifications.api'
import PlusIcon from '@/assets/icons/add-plus.svg?component'
import DownloadIcon from '@/assets/icons/download.svg?component'
import LoadingIcon from '@/assets/icons/loading.svg?component'
import CampaignDeletePopup from '@/components/CampaignDeletePopup/CampaignDeletePopup.vue'
import CampaignListItem from '@/components/CampaignListItem/CampaignListItem.vue'
import CampaignOverview from '@/components/CampaignOverview/CampaignOverview.vue'
import { FilterComponent } from '@/components/Filter'
import HeaderComponent from '@/components/Navigation/HeaderComponent.vue'
import { makeQueryStringForCampaigns } from '@/lib/adapters/campaigns.adapter'
import { notification } from '@/lib/helpers'
import { type CampaignsFilterType, CampaignStatusEnum } from '@/lib/types/campaigns.types'
import { useUserStore } from '@/stores/user'
import { BoxComponent, ButtonComponent, FlexComponent, PageWrapperComponent, TypographyComponent } from '@/ui-library'

const router = useRouter()
const route = useRoute()
const isDeletePopupOpen = ref(false)
const isCampaignOverviewOpen = ref(false)
const deleteCampaignId = ref<number | undefined>()
const userStore = useUserStore()
const { filter, reset } = useQueryFilter<CampaignsFilterType>(
  {
    page: commonFilterFields.page,
    sortBy: commonFilterFields.sortBy,
    query: commonFilterFields.query,
    advertiserGroup: commonFilterFields.advertiserGroup,
    advertiser: commonFilterFields.advertiser,
    statuses: enumArrayField('statuses', Object.values(CampaignStatusEnum)),
  },
  {
    page: 1,
    sortBy: 'created_at:desc',
    advertiserGroup: {
      id: userStore.user.storeGroupId,
      name: '',
    },
    statuses: [CampaignStatusEnum.Active, CampaignStatusEnum.Scheduled],
  },
  { resetPageOn: COMMON_RESET_PAGE_ON, cleared: { page: 1, sortBy: 'created_at:desc' } },
)
const { data, isLoading, totalPages } = useGetCampaigns(filter)
const campaignIds = computed(() => data.value?.map((campaign) => campaign.id) || [])
const { notificationStatuses } = useGetNotificationStatuses(campaignIds)
const { downloadFile, isLoading: isExportLoading, error: exportError } = useFileDownload()

const deleteCampaign = async () => {
  isDeletePopupOpen.value = false
  isCampaignOverviewOpen.value = false
  refreshList()
  return router.push({
    query: {
      ...route.query,
      campaignId: undefined,
    },
  })
}
const openDeleteCampaignPopup = (id: number) => {
  deleteCampaignId.value = id
  isDeletePopupOpen.value = true
}
const openCampaignOverview = (campaignId: number) => router.push({ query: { ...route.query, campaignId } })
const refreshList = () => (filter.value = { ...filter.value })
const queryParamsDownload = computed(() => makeQueryStringForCampaigns({ ...filter.value, format: 'xlsx' }, userStore.id))
const sortingItems = [
  { label: 'Newest first', value: 'created_at:desc' },
  { label: 'Oldest first', value: 'created_at:asc' },
  { label: 'Ends soon', value: 'ends_soon' },
]
const moreFiltersItems = [
  { value: CampaignStatusEnum.Active, label: 'Active' },
  { value: CampaignStatusEnum.Scheduled, label: 'Scheduled' },
  { value: CampaignStatusEnum.Paused, label: 'Paused' },
  { value: CampaignStatusEnum.Ended, label: 'Ended' },
]
watch(
  () => route.query.campaignId,
  (campaignId) => {
    isCampaignOverviewOpen.value = Boolean(campaignId)
  },
  { immediate: true },
)

const handleExport = async () => {
  await downloadFile(
    `/v2/offers?include[]=trafficSourcesCosts&include[]=store&${queryParamsDownload.value}`,
    `campaigns-export-${new Date().toISOString().split('T')[0]}.xlsx`,
  )
  notification.success('Export completed successfully')
}

watch(
  () => exportError.value,
  () => {
    if (exportError.value) {
      notification.error('Failed to export campaigns list')
    }
  },
)
</script>
<template>
  <HeaderComponent>
    <FlexComponent :gap="16">
      <ButtonComponent variant="transparent" :is-loading="isExportLoading" @click="handleExport">
        <template #iconLeft>
          <el-icon :size="18">
            <DownloadIcon class="campaign-view__download-icon" />
          </el-icon>
        </template>
        Export list
      </ButtonComponent>
      <ButtonComponent variant="secondary" to="/campaigns/create">
        <template #iconLeft>
          <el-icon :size="18">
            <PlusIcon />
          </el-icon>
        </template>
        Create campaign
      </ButtonComponent>
    </FlexComponent>
  </HeaderComponent>
  <main id="campaigns-page" class="campaign-view__wrapper">
    <PageWrapperComponent>
      <BoxComponent :mb="32">
        <FilterComponent
          v-model="filter"
          :sorting-items="sortingItems"
          :more-filters-items="moreFiltersItems"
          :has-separate-order-sorting="false"
          show-advertiser-selector
          show-more-filters-selector
          show-sorting-selector
          placeholder="Search campaigns"
          @clear="reset"
        />
      </BoxComponent>
      <FlexComponent v-if="!isLoading && data && data?.length === 0" justify="center">
        <TypographyComponent variant="body-large"> There are no campaigns that match your filters </TypographyComponent>
      </FlexComponent>
      <div v-if="isLoading" class="campaign-view__loading">
        <el-icon :size="96">
          <LoadingIcon />
        </el-icon>
      </div>
      <FlexComponent v-if="!isLoading && data && data?.length > 0" :gap="16" direction="column">
        <CampaignListItem
          v-for="item in data"
          :key="item.id"
          :item="item"
          :has-update="notificationStatuses.has(item.id)"
          @on-delete="openDeleteCampaignPopup"
          @on-open="openCampaignOverview"
        />
      </FlexComponent>
    </PageWrapperComponent>
    <BoxComponent v-if="totalPages !== 0 && data && data?.length > 0" :pt="32" :pb="32" class="campaign-view__pagination">
      <PaginationComponent v-model:current-page="filter.page" :total-pages="totalPages" />
    </BoxComponent>
    <CampaignDeletePopup v-model="isDeletePopupOpen" :campaign-id="deleteCampaignId" @on-delete="deleteCampaign" />
    <CampaignOverview
      v-if="isCampaignOverviewOpen"
      v-model="isCampaignOverviewOpen"
      @on-refresh="refreshList"
      @on-delete="openDeleteCampaignPopup"
    />
  </main>
</template>
<style lang="scss" scoped>
.campaign-view__wrapper {
  position: relative;
}
.campaign-view__loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.campaign-view__pagination {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}

:deep(.campaign-view__download-icon) {
  path {
    fill: var(--inverted);
  }
}
</style>
